import { isElementInViewport } from '@theme/utilities';

/*
  Barre d'achat fixe — refonte du 27/10/2026 (benchmark ManoMano, finding #1).

  Avant : la barre s'activait dès que le formulaire n'était pas à l'écran, y compris quand il
  était SOUS le premier écran (cas de toutes les fiches mobiles). Comme ce composant enveloppe
  le vrai bloc d'achat (menus Itoris compris), il passait en position fixe avec les menus :
  230 px sur mobile, 240 px sur desktop, par-dessus le titre et le prix dès l'arrivée.

  Maintenant :
  - elle ne s'active qu'une fois le bloc d'achat DÉPASSÉ (passé sous le header collé) ;
  - le formulaire garde sa hauteur pendant ce temps (pas de saut de page) ;
  - en mode barre, les menus Itoris et le sélecteur de quantité sont masqués par CSS
    (assets/ep-pdp.css) : il reste le prix, le rappel de la config et le bouton ;
  - si une option obligatoire est vide au clic, on remonte au formulaire au lieu d'afficher
    une erreur invisible.
*/
class StickyAddToCart extends HTMLElement {
  constructor() {
    super();

    this.form = null;
    this.footer = null;
    this.header = null;
    this.naturalHeight = 0;
    this.onScroll = () => this.#displayStickyAddToCart();
    this.onClick = (event) => this.#guardIncompleteOptions(event);
  }

  connectedCallback() {
    this.form = this.closest('[data-ref="product-form"]');
    this.footer = document.querySelector('[data-ref="footer"]');
    this.header = this.#findStickyHeader();
    this.#addSummary();

    this.#displayStickyAddToCart();

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    // Phase de capture : passe avant le gestionnaire d'ajout au panier d'Itoris sur le bouton
    this.addEventListener('click', this.onClick, true);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.removeEventListener('click', this.onClick, true);
  }

  // Le header du thème est un groupe de sections en position sticky
  #findStickyHeader() {
    const sections = document.querySelectorAll('.shopify-section-group-header-group');
    for (const section of sections) {
      if (getComputedStyle(section).position === 'sticky') return section;
    }
    return null;
  }

  // Bas du header collé (0 si aucun) : la barre s'active quand le bloc d'achat passe dessous
  #headerBottom() {
    if (!this.header) return 0;
    return Math.max(0, this.header.getBoundingClientRect().bottom);
  }

  // Prix + rappel de config, affichés seulement en mode barre (CSS)
  #addSummary() {
    if (this.querySelector('.sticky-add-to-cart__summary')) return;
    const summary = document.createElement('div');
    summary.className = 'sticky-add-to-cart__summary';
    summary.innerHTML =
      '<strong class="sticky-add-to-cart__price"></strong><small class="sticky-add-to-cart__recap" data-dpo-recap></small>';
    const content = this.querySelector('.sticky-add-to-cart__content');
    if (content) content.prepend(summary);
  }

  // Prix HT affiché par le bloc prix custom de la fiche (#buy-price-ht, suit les options Itoris ;
  // HT en premier depuis le 27/10/2026), sinon prix TTC de la variante porté par le bouton.
  #refreshPrice() {
    const target = this.querySelector('.sticky-add-to-cart__price');
    if (!target) return;
    const ht = document.getElementById('buy-price-ht');
    if (ht && ht.textContent.trim()) {
      target.textContent = `${ht.textContent.trim()} HT`;
      return;
    }
    const container = this.querySelector('[data-ref="add-to-cart-button-container"]');
    const cents = container ? parseInt(container.dataset.price, 10) : NaN;
    target.textContent = Number.isNaN(cents)
      ? ''
      : `${(cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })} TTC`;
  }

  #displayStickyAddToCart() {
    if (!this.form) return;
    const active = this.dataset.active === 'true';

    // Hauteur naturelle du bloc, mesurée tant qu'il est dans le flux (les menus Itoris
    // arrivent après le chargement : on la remet à jour à chaque passage).
    if (!active) this.naturalHeight = this.offsetHeight;

    const passed = this.form.getBoundingClientRect().bottom < this.#headerBottom();
    const footerVisible = this.footer ? isElementInViewport(this.footer) : false;
    const shouldBeActive = passed && !footerVisible;

    if (shouldBeActive === active) return;

    if (shouldBeActive) {
      // Le bloc quitte le flux : le formulaire garde sa place pour éviter un saut de page
      this.form.style.minHeight = `${this.naturalHeight}px`;
      this.#refreshPrice();
      this.dataset.active = 'true';
      this.classList.add('color-scheme-1');
    } else {
      this.dataset.active = 'false';
      this.classList.remove('color-scheme-1');
      this.form.style.minHeight = '';
    }
  }

  // Option Itoris obligatoire encore vide (champ affiché dans la cascade, pas une branche cachée)
  #firstEmptyRequiredOption() {
    const fields = this.querySelectorAll('#itoris_dynamicproductoptions .field[id^="dynamic_option_id_"]');
    for (const field of fields) {
      if (field.classList.contains('ihidden') || field.style.display === 'none') continue;
      const select = field.querySelector('select.product-custom-option');
      if (!select || select.disabled || select.value) continue;
      if (field.querySelector('label.required')) return field;
    }
    return null;
  }

  #guardIncompleteOptions(event) {
    if (this.dataset.active !== 'true') return;
    if (!event.target.closest('[data-ref="add-to-cart-button"]')) return;
    const field = this.#firstEmptyRequiredOption();
    if (!field) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    // Retour au formulaire : la barre se désactive d'elle-même quand le bloc revient à l'écran
    this.form.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusable = field.querySelector('.dpo-cartes input, select');
    if (focusable) setTimeout(() => focusable.focus({ preventScroll: true }), 600);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
